import React, { useEffect, useState } from 'react';
import { Modal, Slide } from '@mui/material';
import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { MeetingNoteTablou } from '../../types';
import { logMeetingNotesAdded } from '../../utils/activityLogger';
import {
  formatTabloulContent,
  isTabloulEmpty,
  parseMeetingNoteTablou,
} from '../MeetingNotes/meetingNotesUtils';
import { parseSessionNumber } from '../SessionReports/sessionReportsUtils';
import { t } from '../../utils/translations';
import Button from '../ui/Button';
import { SLIDE_TIMEOUT } from '../common/SlideUpTransition';

interface TabloulDialogProps {
  open: boolean;
  onClose: () => void;
  /** Fired after a successful save, with the session the tablou belongs to. */
  onSaved?: (sessionNumber: number) => void;
  caseId: string;
  /** Case's short title, for the activity log — matches what every other note logs. */
  caseTitle: string;
  caseName: string;
  sessionNumber: number;
}

type TabloulField = keyof MeetingNoteTablou;

const LAYERS: {
  key: TabloulField;
  num: number;
  title: string;
  label: string;
  hint: string;
}[] = [
  {
    key: 'situatia',
    num: 1,
    title: t.tablou.situatiaTitle,
    label: t.tablou.situatiaLabel,
    hint: t.tablou.situatiaHint,
  },
  {
    key: 'raspunsurile',
    num: 2,
    title: t.tablou.raspunsurileTitle,
    label: t.tablou.raspunsurileLabel,
    hint: t.tablou.raspunsurileHint,
  },
  {
    key: 'gandurile',
    num: 3,
    title: t.tablou.gandurileTitle,
    label: t.tablou.gandurileLabel,
    hint: t.tablou.gandurileHint,
  },
  {
    key: 'motivele',
    num: 4,
    title: t.tablou.motiveleTitle,
    label: t.tablou.motiveleLabel,
    hint: t.tablou.motiveleHint,
  },
];

const FIELD =
  'mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-base text-slate-900 outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 disabled:opacity-50 sm:text-sm resize-y';

const EMPTY: MeetingNoteTablou = {};

/**
 * The deep-dive companion to the quick memento: one specific session, Tripp's four layers, saved
 * as a meeting note so it lives exactly where every other note does — visible to the counselor
 * and their mentor, never in the leader's report. The self-check questions live in the memento
 * itself, as prompts rather than fields here.
 *
 * Reached as much from "Mai târziu" as from "Clarific acum", so reopening the same session has to
 * resume the same note rather than start a second one — the whole point is picking back up.
 */
const TabloulDialog: React.FC<TabloulDialogProps> = ({
  open,
  onClose,
  onSaved,
  caseId,
  caseTitle,
  caseName,
  sessionNumber,
}) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [existingNoteId, setExistingNoteId] = useState<string | null>(null);
  const [fields, setFields] = useState<MeetingNoteTablou>(EMPTY);
  const [sessionTheme, setSessionTheme] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open || !caseId) return;
    let cancelled = false;
    // Cleared before the read, not after it: whatever is in here belongs to the previous case
    // and session, and both showing it and — far worse — saving over the note it came from are
    // one failed read away if it is left standing.
    setExistingNoteId(null);
    setFields(EMPTY);
    setSessionTheme('');
    setLoading(true);
    setLoadFailed(false);
    setError(false);
    (async () => {
      try {
        // Same single-field index every other per-case note read already relies on — narrowing
        // to this session, and to the one note that is a tablou, happens client-side rather than
        // adding a composite index for a query this small (one case's notes). The report read
        // alongside it is just as small, and only for the session's own theme in the header.
        const [notesSnap, reportsSnap] = await Promise.all([
          getDocs(query(collection(db, 'meetingNotes'), where('caseId', '==', caseId))),
          getDocs(query(collection(db, 'sessionReports'), where('caseId', '==', caseId))),
        ]);
        if (cancelled) return;
        let found: { id: string; tablou: MeetingNoteTablou } | null = null;
        for (const docSnap of notesSnap.docs) {
          const data = docSnap.data();
          if (data.sessionNumber !== sessionNumber) continue;
          const tablou = parseMeetingNoteTablou(data);
          if (tablou) {
            found = { id: docSnap.id, tablou };
            break;
          }
        }
        const reportDoc = reportsSnap.docs.find(
          (docSnap) => parseSessionNumber(docSnap.data().sessionNumber) === sessionNumber
        );
        const theme = reportDoc?.data().mainTheme;
        if (cancelled) return;
        setExistingNoteId(found?.id ?? null);
        setFields(found?.tablou ?? EMPTY);
        setSessionTheme(typeof theme === 'string' ? theme.trim() : '');
      } catch (err) {
        console.error('Error loading the tablou:', err);
        // Saving now would either duplicate an existing tablou or overwrite it with a blank one,
        // since we no longer know which is the case.
        if (!cancelled) setLoadFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, caseId, sessionNumber]);

  const setField = (key: TabloulField, value: string) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (saving || loading || loadFailed) return;
    setSaving(true);
    setError(false);
    try {
      const trimmed: MeetingNoteTablou = {};
      (Object.keys(fields) as TabloulField[]).forEach((key) => {
        const value = (fields[key] ?? '').trim();
        if (value) trimmed[key] = value;
      });
      const content = formatTabloulContent(trimmed);

      if (existingNoteId) {
        await updateDoc(doc(db, 'meetingNotes', existingNoteId), {
          tablou: trimmed,
          content,
          sessionNumber,
          updatedAt: new Date(),
        });
      } else {
        const newDoc = await addDoc(collection(db, 'meetingNotes'), {
          caseId,
          sessionNumber,
          tablou: trimmed,
          content,
          createdBy: currentUser?.id || '',
          createdByName: currentUser?.fullName || '',
          createdAt: new Date(),
          updatedAt: new Date(),
        });
        setExistingNoteId(newDoc.id);
        if (currentUser) {
          await logMeetingNotesAdded(
            caseId,
            caseTitle,
            currentUser.id,
            currentUser.fullName || currentUser.email || 'Unknown User'
          );
        }
      }
      // Closing is the confirmation, same as the plain note form.
      onSaved?.(sessionNumber);
      onClose();
    } catch (err) {
      console.error('Error saving the tablou:', err);
      setError(true);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) onClose();
  };

  const canSave = !loading && !saving && !loadFailed && !isTabloulEmpty(fields);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      aria-labelledby="tablou-title"
      // Above the note dialogs (1400): the tablou is opened from inside one of them, from the
      // badge on its own note, so it can never be the one underneath.
      sx={{ zIndex: 1500 }}
      slotProps={{
        backdrop: {
          timeout: SLIDE_TIMEOUT,
          sx: { backgroundColor: 'rgba(15, 23, 42, 0.4)' },
        },
      }}
    >
      <Slide direction="up" in={open} timeout={SLIDE_TIMEOUT}>
        <div className="fixed inset-0 flex items-stretch justify-center outline-none sm:items-center sm:p-4">
          {/* Same box as "Pregătește sesiunea" and "Întrebări ajutătoare" on desktop: MUI's `sm`
              breakpoint (600px) and the shared `DIALOG_MAX_HEIGHT` (85vh). */}
          <div className="safe-pt safe-pb relative flex h-full w-full flex-col overflow-hidden bg-white shadow-lg sm:h-auto sm:max-h-[85vh] sm:max-w-[37.5rem] sm:rounded-xl sm:border sm:border-slate-200">
            <div className="flex shrink-0 items-start gap-3 border-b border-slate-100 px-4 pb-3 pt-4 sm:px-5 sm:pt-5">
              <div className="min-w-0 flex-1">
                <h2 id="tablou-title" className="text-base font-semibold text-slate-900">
                  {t.tablou.title}
                </h2>
                <p className="mt-1 truncate text-sm text-slate-500">
                  {caseName} · {t.sessionReports.sessionNumber} {sessionNumber}
                  {sessionTheme && ` · ${sessionTheme}`}
                </p>
              </div>
              <button
                type="button"
                onClick={handleClose}
                aria-label={t.common.close}
                className="shrink-0 rounded-md p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              <div className="flex items-start gap-2 border-b border-slate-100 bg-slate-50 px-4 py-2.5 sm:px-5">
                <InformationCircleIcon
                  className="mt-0.5 h-4 w-4 shrink-0 text-slate-400"
                  aria-hidden="true"
                />
                <p className="text-xs leading-[17px] text-slate-600">
                  <strong className="font-semibold text-slate-700">
                    {t.tablou.bannerBold}
                  </strong>{' '}
                  {t.tablou.bannerTail}
                </p>
              </div>

              <div className="flex flex-col gap-[18px] px-4 py-4 sm:px-5">
                {LAYERS.map((layer) => (
                  <div key={layer.key}>
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-brand-600">
                      <span className="flex h-[17px] w-[17px] shrink-0 items-center justify-center rounded-full bg-brand-100 text-[10px] font-bold text-brand-700">
                        {layer.num}
                      </span>
                      {layer.title}
                    </span>
                    <label className="mt-1.5 block text-sm font-semibold text-slate-900">
                      {layer.label}
                    </label>
                    <span className="mt-0.5 block text-xs text-slate-500">{layer.hint}</span>
                    <textarea
                      value={fields[layer.key] ?? ''}
                      onChange={(e) => setField(layer.key, e.target.value)}
                      disabled={saving || loading}
                      rows={2}
                      className={FIELD}
                    />
                  </div>
                ))}
              </div>

              {(error || loadFailed) && (
                <p className="px-4 pb-3 text-xs text-red-600 sm:px-5">
                  {loadFailed ? t.tablou.loadError : t.tablou.saveError}
                </p>
              )}
            </div>

            <div className="flex shrink-0 items-center gap-2 border-t border-slate-100 px-4 py-3 sm:px-5">
              <div className="ml-auto">
                <Button
                  variant="primary"
                  onClick={() => void handleSave()}
                  disabled={!canSave}
                  loading={saving}
                >
                  {t.tablou.save}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Slide>
    </Modal>
  );
};

export default TabloulDialog;
